import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Popup } from '../../../../components/Popup'
import { Typography } from '../../../../components/Typography'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import {
  initiateAdminPayment,
  initiateCognusARPayment,
  initiateRebatesPayment
} from './api'
import { currencyFormatter } from '../../../ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { setLoading } from '../../../../redux/store/common/slice'
import { notification } from '../../../../components/Notification'
import { BTN_TXT, VALIDATION_MESSAGES } from '../../../../constants'
import { ConfirmPaymentPopupProps } from './types'
import { PAYMENT_TYPES } from '../../types'
import './styles.scss'

export const ConfirmPaymentPopup = ({
  type,
  selectedLength,
  selectedRowData,
  onResetSelection,
  updateTableData,
  showBankAccountError
}: ConfirmPaymentPopupProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [showMoreInfo, setShowMoreInfo] = useState(false)
  const tooltipText = useMemo(
    () =>
      showBankAccountError
        ? 'Please create bank account to initiate a payment.'
        : 'Payment cannot be initiated for multiple reports.',
    [showBankAccountError]
  )

  const popupText = useMemo(
    () =>
      selectedRowData &&
      (type === PAYMENT_TYPES.ADMIN_FEE
        ? `${type} of ${currencyFormatter(
            selectedRowData?.[0].total
          )} will be paid to ${
            selectedRowData?.[0]?.name
          }. Do you want to proceed?`
        : `${type} of ${currencyFormatter(
            selectedRowData.total
          )}$ will be paid to OM Community. Click 'Confirm' to authorize this payment.`),
    [type, selectedRowData]
  )
  const dispatch = useDispatch()

  const onCancel = () => {
    onResetSelection()
    setIsOpen(false)
  }

  const onProceedPayment = async () => {
    try {
      dispatch(setLoading(true))
      let request = initiateCognusARPayment //default value
      if (type === PAYMENT_TYPES.REBATES) {
        request = initiateRebatesPayment
      } else if (type === PAYMENT_TYPES.ADMIN_FEE) {
        request = initiateAdminPayment
      }
      await request(String(selectedRowData?.uuid || selectedRowData?.[0]?.uuid))
      notification.success({
        message: VALIDATION_MESSAGES.SM0164
      })
    } finally {
      onCancel()
      await updateTableData()
    }
  }

  return (
    <>
      <Popup
        visible={isOpen}
        onCancel={onCancel}
        title={
          type === PAYMENT_TYPES.ADMIN_FEE
            ? `Pay ${type}`
            : `Confirm ${type} payment`
        }
        width={442}
        footer={
          <>
            <Button onClick={onCancel} type={BUTTON_TYPES.DEFAULT} upperCase>
              {BTN_TXT.CANCEL}
            </Button>
            <Button
              type={BUTTON_TYPES.PRIMARY}
              onClick={onProceedPayment}
              upperCase
            >
              {type === PAYMENT_TYPES.ADMIN_FEE
                ? BTN_TXT.PROCEED
                : BTN_TXT.CONFIRM}
            </Button>
          </>
        }
      >
        <div className="gap-16 column">
          <Typography.Body1>{popupText}</Typography.Body1>
          {showMoreInfo && type !== PAYMENT_TYPES.ADMIN_FEE && (
            <Typography.Body2 className="show-more-info__text">
              By clicking Confirm you authorize OM Community to debit your bank
              account via ACH. You authorize, if necessary, adjustments for any
              debit entries made in error to your account. This authority will
              remain in effect until your OM Community account has been
              terminated or you update your payment preferences or we are
              notified by you in writing to cancel it in such time as to afford
              OM Community commercially reasonable opportunity to act on it. You
              represent and warrant that LINKED BANK ACCOUNT details referenced
              herein belong to you and you have the authority to provide OM
              Community with this authorization to credit or debit your account.
            </Typography.Body2>
          )}
          {type !== PAYMENT_TYPES.ADMIN_FEE && (
            <Typography.Body2
              className="show-more-info__button"
              onClick={() => setShowMoreInfo(!showMoreInfo)}
            >
              {showMoreInfo ? 'Hide' : 'Show'} more information
            </Typography.Body2>
          )}
        </div>
      </Popup>
      {selectedLength > 1 || showBankAccountError ? (
        <Tooltip title={tooltipText}>
          <div>
            <Button type={BUTTON_TYPES.PRIMARY} disabled upperCase>
              {BTN_TXT.PAY}
            </Button>
          </div>
        </Tooltip>
      ) : (
        <Button
          type={BUTTON_TYPES.PRIMARY}
          onClick={() => setIsOpen(true)}
          upperCase
        >
          {BTN_TXT.PAY}
        </Button>
      )}
    </>
  )
}
